// ============================================================================
// FSA TEMPLATE SERVICE
// ============================================================================
// Read-only service for FSA Templates (Stage 1).
//
// Backend wraps every response in ApiResponse<T>:
//   { success, message, data, errors, correlationId, timestamp }
//
// The list endpoint additionally wraps data in PagedResult<T>:
//   data: { items, page, pageSize, totalCount, totalPages, hasPrevious, hasNext }
//
// `getAll()` transforms PagedResult<T> → frontend PaginatedResponse<T>
// (per prima-web/CLAUDE.md "Service layer transformation").

import { get } from '../../client/axiosClient';
import { fsaTemplateEndpoints } from '../../endpoints/fsa/fsaTemplate.endpoints';
import { buildEndpointWithQuery } from '../../endpoints/base.endpoints';
import type { ApiResponse, PaginatedResponse, ID } from '../../types';
import type {
  FsTemplate,
  FsTemplateHierarchy,
  FsTemplateSearchRequest,
} from '../../types/fsa/template.types';

class FsaTemplateService {
  /**
   * Paged list of template headers (Template Browser).
   * Transforms backend PagedResult<T> → frontend PaginatedResponse<T>.
   */
  async getAll(
    params?: FsTemplateSearchRequest
  ): Promise<PaginatedResponse<FsTemplate>> {
    const endpoint = params
      ? buildEndpointWithQuery(
          fsaTemplateEndpoints.basePath(),
          params as Record<string, any>
        )
      : fsaTemplateEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  /** Single header (no children). */
  async getById(id: ID): Promise<ApiResponse<FsTemplate>> {
    const response = await get<ApiResponse<FsTemplate>>(
      fsaTemplateEndpoints.getById(id)
    );
    return response.data;
  }

  /**
   * Full hierarchy (template + reports + schedules + sections + classes +
   * narratives + format definitions). Hydrated by the backend in one round
   * trip — used by the Template View Modal.
   */
  async getFullHierarchy(id: ID): Promise<ApiResponse<FsTemplateHierarchy>> {
    const response = await get<ApiResponse<FsTemplateHierarchy>>(
      fsaTemplateEndpoints.getFullHierarchy(id)
    );
    return response.data;
  }

  /**
   * Server-rendered Preview PDF for the View Modal's "Preview & Print"
   * action. Returns a raw Blob (Content-Type: application/pdf) that the
   * modal renders inside an &lt;iframe&gt; via URL.createObjectURL — the
   * browser's native PDF viewer handles printing / saving reliably.
   */
  async getPreviewPdfBlob(
    id: ID,
    opts?: { periodEnd?: string; mode?: 'actual' | 'rounded' }
  ): Promise<Blob> {
    const base = fsaTemplateEndpoints.getPreviewPdf(id);
    const params: string[] = [];
    if (opts?.periodEnd) params.push(`periodEnd=${encodeURIComponent(opts.periodEnd)}`);
    if (opts?.mode)      params.push(`mode=${encodeURIComponent(opts.mode)}`);
    const url = params.length > 0 ? `${base}?${params.join('&')}` : base;
    const response = await get<Blob>(url, { responseType: 'blob' });
    return response.data;
  }
}

export const fsaTemplateService = new FsaTemplateService();
export default fsaTemplateService;
